import { Fragment, useMemo, useState } from "react";

import type { OpeningHoursBlock } from "@/lib/types";

export const WEEKDAYS = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
export const MAX_BLOCKS_PER_DAY = 3;
const WORKDAYS = [0, 1, 2, 3, 4];

export type GridCell = { openTime?: string; closeTime?: string };

const TIME_PATTERN = /^(\d{1,2})(?:[:.,h ]?(\d{2}))?(?::\d{2})?$/;

/** `7` → `07:00`, `730` / `7.30` / `7:30` → `07:30`; null when it is not a time of day. */
export const parseTime = (input: string): string | null => {
  const match = TIME_PATTERN.exec(input.trim());
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2] ?? "0");
  if (hours > 23 || minutes > 59) {
    return null;
  }
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const emptyGrid = (): GridCell[][] =>
  Array.from({ length: WEEKDAYS.length }, () =>
    Array.from({ length: MAX_BLOCKS_PER_DAY }, () => ({})),
  );

const isCellEmpty = (cell: GridCell): boolean =>
  !cell.openTime && !cell.closeTime;

export const toGrid = (blocks: OpeningHoursBlock[]): GridCell[][] => {
  const grid = emptyGrid();
  for (const b of blocks) {
    if (b.weekday < 0 || b.weekday >= WEEKDAYS.length) {
      continue;
    }
    if (b.blockIndex < 0 || b.blockIndex >= MAX_BLOCKS_PER_DAY) {
      continue;
    }
    grid[b.weekday][b.blockIndex] = {
      openTime: parseTime(b.openTime) ?? b.openTime,
      closeTime: parseTime(b.closeTime) ?? b.closeTime,
    };
  }
  return grid;
};

export const fromGrid = (grid: GridCell[][]): OpeningHoursBlock[] =>
  grid.flatMap((row, weekday) =>
    row.flatMap((cell, blockIndex) => {
      const openTime = parseTime(cell.openTime ?? "");
      const closeTime = parseTime(cell.closeTime ?? "");
      return openTime && closeTime
        ? [{ weekday, blockIndex, openTime, closeTime }]
        : [];
    }),
  );

const cellError = (cell: GridCell): string | undefined => {
  if (isCellEmpty(cell)) {
    return undefined;
  }
  if (!cell.openTime || !cell.closeTime) {
    return "Doplňte oba časy";
  }
  const openTime = parseTime(cell.openTime);
  const closeTime = parseTime(cell.closeTime);
  if (!openTime || !closeTime) {
    return "Neplatný čas";
  }
  if (openTime >= closeTime) {
    return "Začátek musí být před koncem";
  }
  return undefined;
};

/** One message per cell, undefined where the cell can be saved as it is. */
export const gridErrors = (grid: GridCell[][]): (string | undefined)[][] =>
  grid.map((row) => {
    const errors = row.map(cellError);
    const valid = row.flatMap((cell, block) =>
      errors[block] || isCellEmpty(cell)
        ? []
        : [
            {
              block,
              openTime: parseTime(cell.openTime ?? "") ?? "",
              closeTime: parseTime(cell.closeTime ?? "") ?? "",
            },
          ],
    );
    for (const a of valid) {
      for (const b of valid) {
        if (
          a.block !== b.block &&
          a.openTime < b.closeTime &&
          b.openTime < a.closeTime
        ) {
          errors[a.block] = "Překrývá se s jiným blokem";
        }
      }
    }
    return errors;
  });

export const hasGridErrors = (grid: GridCell[][]): boolean =>
  gridErrors(grid).some((row) => row.some(Boolean));

const TrashIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    className="h-4 w-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4h6v3"
    />
  </svg>
);

const CopyIcon = () => (
  <svg
    aria-hidden="true"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    className="h-4 w-4"
  >
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1"
    />
  </svg>
);

const TimeInput = ({
  value,
  label,
  invalid,
  onValueChange,
}: {
  value: string;
  label: string;
  invalid: boolean;
  onValueChange: (value: string) => void;
}) => {
  const handleBlur = () => {
    const parsed = parseTime(value);
    if (parsed && parsed !== value) {
      onValueChange(parsed);
    }
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      placeholder="7:30"
      aria-label={label}
      aria-invalid={invalid}
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      onBlur={handleBlur}
      className={`w-16 rounded border px-2 py-1 text-sm ${
        invalid ? "border-red-400 bg-red-50" : "border-gray-300"
      }`}
    />
  );
};

const BlockCell = ({
  weekday,
  block,
  blockLabel,
  cell,
  error,
  onCellChange,
}: {
  weekday: number;
  block: number;
  blockLabel: string;
  cell: GridCell;
  error: string | undefined;
  onCellChange: (weekday: number, block: number, patch: GridCell) => void;
}) => {
  const label = `${WEEKDAYS[weekday]}, ${blockLabel.toLowerCase()} ${block + 1}`;

  return (
    <td className="py-2 pr-4 align-top">
      <div className="flex items-center gap-1">
        <TimeInput
          value={cell.openTime ?? ""}
          label={`${label}, od`}
          invalid={Boolean(error)}
          onValueChange={(openTime) =>
            onCellChange(weekday, block, { openTime })
          }
        />
        <span className="text-gray-400">–</span>
        <TimeInput
          value={cell.closeTime ?? ""}
          label={`${label}, do`}
          invalid={Boolean(error)}
          onValueChange={(closeTime) =>
            onCellChange(weekday, block, { closeTime })
          }
        />
        <button
          type="button"
          onClick={() =>
            onCellChange(weekday, block, {
              openTime: undefined,
              closeTime: undefined,
            })
          }
          disabled={isCellEmpty(cell)}
          title="Smazat blok"
          aria-label={`Smazat ${label}`}
          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:invisible"
        >
          <TrashIcon />
        </button>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </td>
  );
};

const CopyDayPanel = ({
  source,
  onCopy,
  onCancel,
}: {
  source: number;
  onCopy: (source: number, targets: number[]) => void;
  onCancel: () => void;
}) => {
  const [targets, setTargets] = useState<number[]>([]);
  const others = WEEKDAYS.map((_, i) => i).filter((i) => i !== source);

  const toggle = (day: number) =>
    setTargets((t) =>
      t.includes(day) ? t.filter((d) => d !== day) : [...t, day],
    );

  return (
    <div className="flex flex-wrap items-center gap-3 rounded bg-gray-50 px-3 py-2 text-sm">
      <span className="text-gray-700">Zkopírovat {WEEKDAYS[source]} do:</span>
      {others.map((day) => (
        <label key={day} className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={targets.includes(day)}
            onChange={() => toggle(day)}
          />
          {WEEKDAYS[day]}
        </label>
      ))}
      <button
        type="button"
        onClick={() => setTargets(WORKDAYS.filter((d) => d !== source))}
        className="text-xs text-[#1a2b6b] underline"
      >
        Pracovní dny
      </button>
      <button
        type="button"
        onClick={() => setTargets(others)}
        className="text-xs text-[#1a2b6b] underline"
      >
        Celý týden
      </button>
      <div className="ml-auto flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-100"
        >
          Zrušit
        </button>
        <button
          type="button"
          onClick={() => onCopy(source, targets)}
          disabled={targets.length === 0}
          className="rounded bg-[#1a2b6b] px-2 py-1 text-xs font-medium text-white hover:bg-[#0f1d52] disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          Kopírovat
        </button>
      </div>
    </div>
  );
};

/** The 7 × 3 weekly grid shared by opening hours and transfer windows. Changes stay local until the section saves. */
export const WeeklyBlocksEditor = ({
  grid,
  onChange,
  blockLabel,
}: {
  grid: GridCell[][];
  onChange: (grid: GridCell[][]) => void;
  blockLabel: string;
}) => {
  const [copySource, setCopySource] = useState<number | null>(null);
  const errors = useMemo(() => gridErrors(grid), [grid]);
  const isEmpty = grid.every((row) => row.every(isCellEmpty));

  const updateCell = (weekday: number, block: number, patch: GridCell) => {
    const normalised = Object.fromEntries(
      Object.entries(patch).map(([key, value]) => [key, value || undefined]),
    );
    onChange(
      grid.map((row, i) =>
        i === weekday
          ? row.map((cell, j) =>
              j === block ? { ...cell, ...normalised } : cell,
            )
          : row,
      ),
    );
  };

  const copyDay = (source: number, targets: number[]) => {
    onChange(
      grid.map((row, i) =>
        targets.includes(i) ? grid[source].map((cell) => ({ ...cell })) : row,
      ),
    );
    setCopySource(null);
  };

  const clearAll = () => {
    if (
      window.confirm(
        "Smazat všechny časy v tabulce? Změna se projeví až po uložení.",
      )
    ) {
      onChange(emptyGrid());
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
              <th className="py-2">Den</th>
              {Array.from({ length: MAX_BLOCKS_PER_DAY }).map((_, i) => (
                <th key={i} className="py-2">
                  {blockLabel} {i + 1}
                </th>
              ))}
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {WEEKDAYS.map((label, weekday) => (
              <Fragment key={weekday}>
                <tr className="border-t border-gray-100">
                  <td className="py-2 pr-4 align-top font-medium text-gray-700">
                    <span className="inline-block py-1">{label}</span>
                  </td>
                  {grid[weekday].map((cell, block) => (
                    <BlockCell
                      key={block}
                      weekday={weekday}
                      block={block}
                      blockLabel={blockLabel}
                      cell={cell}
                      error={errors[weekday][block]}
                      onCellChange={updateCell}
                    />
                  ))}
                  <td className="py-2 align-top">
                    <button
                      type="button"
                      onClick={() =>
                        setCopySource(copySource === weekday ? null : weekday)
                      }
                      title="Zkopírovat den do dalších dnů"
                      aria-label={`Zkopírovat ${label} do dalších dnů`}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-[#1a2b6b]"
                    >
                      <CopyIcon />
                    </button>
                  </td>
                </tr>
                {copySource === weekday && (
                  <tr>
                    <td colSpan={MAX_BLOCKS_PER_DAY + 2} className="pb-2">
                      <CopyDayPanel
                        source={weekday}
                        onCopy={copyDay}
                        onCancel={() => setCopySource(null)}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={clearAll}
          disabled={isEmpty}
          className="rounded px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
        >
          Smazat vše
        </button>
      </div>
    </div>
  );
};
